import {
  ChevronDown,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Mails,
  Menu,
  MessageSquare,
  MessageSquarePlus,
  MessagesSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Plane,
  ScanLine,
  Settings,
  ShieldCheck,
  Sprout,
  TicketCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { NAV_LABEL, roleDisplayLabel } from "../lib/labels";

import { Mark } from "./Mark";

/**
 * The team app's frame: sidebar navigation and the main column.
 *
 * Desktop: a labelled sidebar that someone can collapse to an icon rail
 * (remembered in this browser; it starts expanded). Below `md` a sticky
 * top bar with a "Menu" button opens a slide-over with the same
 * navigation, because volunteers run events from their phones.
 *
 * Nav items are filtered against `allowedNav` so people only see pages
 * they can open. Less-used pages sit under a More group. The role badge
 * names the person's access level in plain words.
 */
export type NavId =
  | "overview"
  | "events"
  | "varanasi-trip"
  | "reception"
  | "panel-questions"
  | "members"
  | "emails"
  | "inquiries"
  | "event-feedback"
  | "app-interest"
  | "roles"
  | "feedback"
  | "settings";

interface NavItem {
  id: NavId;
  icon: typeof LayoutDashboard;
  /** "more": tucked under the More group until it's opened. */
  group?: "more";
}

const ALL_NAV_ITEMS: NavItem[] = [
  { id: "overview", icon: LayoutDashboard },
  { id: "events", icon: TicketCheck },
  { id: "varanasi-trip", icon: Plane },
  { id: "reception", icon: ScanLine },
  { id: "panel-questions", icon: MessagesSquare },
  { id: "members", icon: Users },
  { id: "emails", icon: Mails },
  { id: "inquiries", icon: MessageSquare, group: "more" },
  { id: "event-feedback", icon: ClipboardList, group: "more" },
  { id: "app-interest", icon: Sprout, group: "more" },
  { id: "roles", icon: ShieldCheck },
  { id: "feedback", icon: MessageSquarePlus },
  { id: "settings", icon: Settings },
];

const COLLAPSE_KEY = "vy-admin-sidebar-collapsed";
/** The More group's open state, remembered like the sidebar collapse. */
const MORE_KEY = "vy-admin-nav-more-open";

/** A remembered sidebar choice. Some browsers block site storage (private
 *  windows, strict settings) and then reading it throws: the sidebar just
 *  starts expanded with More closed. */
function readRemembered(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function remember(key: string, value: "1" | "0"): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage is blocked: the choice holds until the page reloads.
  }
}

export function Shell({
  active,
  onNavigate,
  allowedNav,
  children,
}: {
  active: NavId;
  onNavigate: (id: NavId) => void;
  allowedNav: NavId[];
  children: ReactNode;
}) {
  const { signOut, role, user } = useAuth();
  const visible = ALL_NAV_ITEMS.filter((item) => allowedNav.includes(item.id));
  const mainItems = visible.filter((item) => !item.group);
  const moreItems = visible.filter((item) => item.group === "more");
  const [collapsed, setCollapsed] = useState(() =>
    readRemembered(COLLAPSE_KEY),
  );
  const [moreOpen, setMoreOpen] = useState(() => readRemembered(MORE_KEY));
  // The active item must always be reachable: if it lives under More,
  // the group opens regardless of the remembered preference.
  const moreForcedOpen = moreItems.some((item) => item.id === active);
  const showMore = moreOpen || moreForcedOpen;
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const homeId: NavId = allowedNav[0] ?? "settings";
  const signedInAs = user?.fullName ?? user?.email ?? null;

  function toggleMore() {
    const next = !moreOpen;
    setMoreOpen(next);
    remember(MORE_KEY, next ? "1" : "0");
  }

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    remember(COLLAPSE_KEY, next ? "1" : "0");
  }

  /** Close the menu and put focus back on the button that opened it. */
  function closeMobile() {
    setMobileOpen(false);
    menuButtonRef.current?.focus();
  }

  // Lock the page scroll while the mobile nav overlays it, and start
  // keyboard and screen reader focus inside the menu.
  useEffect(() => {
    if (!mobileOpen) {
      return;
    }
    closeButtonRef.current?.focus();
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previous;
    };
  }, [mobileOpen]);

  // Escape closes the mobile nav — expected of any modal overlay.
  useEffect(() => {
    if (!mobileOpen) {
      return;
    }
    function onKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setMobileOpen(false);
        menuButtonRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  function navButton(item: NavItem, inMobilePanel: boolean) {
    const isActive = item.id === active;
    const Icon = item.icon;
    const label = NAV_LABEL[item.id];
    const iconOnly = collapsed && !inMobilePanel;
    return (
      <div key={item.id}>
        <button
          type="button"
          onClick={() => {
            onNavigate(item.id);
            setMobileOpen(false);
          }}
          aria-current={isActive ? "page" : undefined}
          aria-label={iconOnly ? label : undefined}
          title={iconOnly ? label : undefined}
          className={`group flex min-h-11 w-full cursor-pointer items-center rounded-lg text-left transition-colors ${
            iconOnly ? "justify-center px-0" : "gap-3 px-3 py-2"
          } ${
            isActive
              ? `bg-[color:var(--color-amber)]/12 font-medium text-[color:var(--color-ink)] ${
                  iconOnly ? "ring-1 ring-[color:var(--color-amber)]/40" : ""
                }`
              : "text-[color:var(--color-ink-soft)] hover:bg-[color:var(--color-bg)]/60 hover:text-[color:var(--color-ink)]"
          }`}
        >
          <Icon
            size={18}
            aria-hidden
            className="shrink-0"
            color={isActive ? "var(--color-amber)" : "var(--color-ink-soft)"}
          />
          {iconOnly ? null : (
            <>
              <span className="text-[15px] leading-snug">{label}</span>
              {isActive ? (
                <span
                  aria-hidden
                  className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: "var(--color-amber)" }}
                />
              ) : null}
            </>
          )}
        </button>
      </div>
    );
  }

  /** Main items, then the More group with its pages beneath when open. */
  function navList(inMobilePanel: boolean) {
    const iconOnly = collapsed && !inMobilePanel;
    return (
      <>
        {mainItems.map((item) => navButton(item, inMobilePanel))}
        {moreItems.length > 0 ? (
          <div className="mt-2 border-t border-[color:var(--color-line)] pt-2">
            <button
              type="button"
              onClick={toggleMore}
              aria-expanded={showMore}
              aria-label={iconOnly ? "More" : undefined}
              title={iconOnly ? "More" : undefined}
              className={`flex min-h-11 w-full cursor-pointer items-center rounded-lg text-left text-[color:var(--color-ink-soft)] transition-colors hover:bg-[color:var(--color-bg)]/60 hover:text-[color:var(--color-ink)] ${
                iconOnly ? "justify-center px-0" : "gap-3 px-3 py-2"
              }`}
            >
              <MoreHorizontal
                size={18}
                aria-hidden
                className="shrink-0"
                color="var(--color-ink-soft)"
              />
              {iconOnly ? null : (
                <>
                  <span className="text-[15px] leading-snug">More</span>
                  <ChevronDown
                    size={16}
                    className={`ml-auto transition-transform ${showMore ? "" : "-rotate-90"}`}
                    aria-hidden
                  />
                </>
              )}
            </button>
            {showMore ? (
              <div className="mt-0.5 space-y-0.5">
                {moreItems.map((item) => navButton(item, inMobilePanel))}
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  const roleBadge = role ? (
    <p className="badge mt-2 border border-[color:var(--color-amber)]/30 bg-[color:var(--color-amber)]/8 text-[color:var(--color-amber)]">
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: "var(--color-amber)" }}
      />
      {roleDisplayLabel(role, CHAPTER.id)}
    </p>
  ) : null;

  const account = role ? (
    <div className="mb-4">
      <p className="text-[14px] text-[color:var(--color-ink-soft)]">
        Signed in as
      </p>
      {signedInAs ? (
        <p className="mt-0.5 text-[15px] font-medium break-words text-[color:var(--color-ink)]">
          {signedInAs}
        </p>
      ) : null}
      {roleBadge}
    </div>
  ) : null;

  /** The chapter name with "Team app" beneath, beside the mark. */
  const wordmark = (
    <div className="flex min-w-0 flex-col">
      <span className="text-[15px] leading-snug font-semibold text-[color:var(--color-ink)]">
        {CHAPTER.name}
      </span>
      <span className="text-[14px] leading-snug text-[color:var(--color-ink-soft)]">
        Team app
      </span>
    </div>
  );

  const signOutClass =
    "inline-flex min-h-11 cursor-pointer items-center rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] hover:bg-[color:var(--color-ink)] hover:text-[color:var(--color-paper)]";

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* ── Mobile top bar (hidden at md+) ── */}
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-[color:var(--color-line)] bg-[color:var(--color-paper)] px-4 py-1.5 md:hidden">
        <button
          type="button"
          onClick={() => onNavigate(homeId)}
          aria-label={`${CHAPTER.name}, go to ${NAV_LABEL[homeId]}`}
          className="flex min-h-11 min-w-0 cursor-pointer items-center gap-2.5 text-left"
        >
          <Mark size={26} />
          <span className="truncate text-[15px] font-semibold text-[color:var(--color-ink)]">
            {CHAPTER.name}
          </span>
        </button>
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-expanded={mobileOpen}
          className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-[color:var(--color-line)] px-3 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:bg-[color:var(--color-bg)]/60"
        >
          <Menu size={20} aria-hidden />
          Menu
        </button>
      </header>

      {/* ── Mobile slide-over navigation ── */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            tabIndex={-1}
            onClick={closeMobile}
            className="absolute inset-0 bg-[color:var(--color-shadow)]/40"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="absolute inset-y-0 left-0 flex w-[min(320px,88vw)] flex-col justify-between overflow-y-auto bg-[color:var(--color-paper)] p-5 shadow-xl"
          >
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <Mark size={28} decorative />
                  {wordmark}
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={closeMobile}
                  className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-[15px] font-medium text-[color:var(--color-ink-soft)] hover:text-[color:var(--color-ink)]"
                >
                  <X size={18} aria-hidden />
                  Close
                </button>
              </div>
              <nav aria-label="Pages" className="mt-6 space-y-1">
                {navList(true)}
              </nav>
            </div>
            <div className="mt-6 border-t border-[color:var(--color-line)] pt-4">
              {account}
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className={`${signOutClass} gap-2 px-4 text-[15px] font-medium`}
              >
                <LogOut size={16} aria-hidden />
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Desktop sidebar — pinned; only the main column scrolls ── */}
      <aside
        className={`sticky top-0 hidden h-screen shrink-0 flex-col justify-between overflow-y-auto border-r border-[color:var(--color-line)] bg-[color:var(--color-paper)] transition-[width] duration-300 md:flex ${
          collapsed ? "w-[76px] p-3.5" : "w-72 p-6"
        }`}
      >
        <div>
          <div
            className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}
          >
            <Mark size={collapsed ? 30 : 36} decorative={!collapsed} />
            {collapsed ? null : wordmark}
          </div>

          <nav
            aria-label="Pages"
            className={`space-y-1 ${collapsed ? "mt-8" : "mt-10"}`}
          >
            {navList(false)}
          </nav>
        </div>

        <div
          className={`mt-6 border-t border-[color:var(--color-line)] ${
            collapsed ? "flex flex-col items-center gap-3 pt-4" : "pt-5"
          }`}
        >
          {collapsed ? null : account}

          <button
            type="button"
            onClick={() => {
              void signOut();
            }}
            aria-label={collapsed ? "Sign out" : undefined}
            title={collapsed ? "Sign out" : undefined}
            className={`${signOutClass} ${
              collapsed
                ? "min-w-11 justify-center"
                : "gap-2 px-4 text-[15px] font-medium"
            }`}
          >
            <LogOut size={16} aria-hidden />
            {collapsed ? null : "Sign out"}
          </button>

          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : undefined}
            title={collapsed ? "Expand sidebar" : undefined}
            className={`inline-flex min-h-11 cursor-pointer items-center rounded-lg text-[color:var(--color-ink-soft)] transition-colors hover:bg-[color:var(--color-bg)]/60 hover:text-[color:var(--color-ink)] ${
              collapsed
                ? "min-w-11 justify-center"
                : "mt-3 gap-2 px-3 text-[14px]"
            }`}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} aria-hidden />
            ) : (
              <>
                <PanelLeftClose size={18} aria-hidden />
                Collapse sidebar
              </>
            )}
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
}
